import {
  DashboardResponse,
  EventsDashboardData,
  EventSummary,
  AgendaItemSummary,
} from '../../../types/dashboard';
import { CalendarDays, Clock, MapPin, ListChecks } from 'lucide-react';

interface EventsPanelProps {
  data: DashboardResponse;
}

const styles = {
  container: {
    padding: 'clamp(16px, 4vw, 48px)',
    overflow: 'hidden' as const,
    height: '100%',
    boxSizing: 'border-box' as const,
    background: '#0d1b12',
    color: '#ffffff',
    fontFamily: "'Inter', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'clamp(8px, 2vw, 24px)',
  },
  heading: {
    fontSize: 'clamp(24px, 4vw, 48px)',
    color: '#c9a84c',
    margin: 0,
    fontWeight: 700,
  },
  columnsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 'clamp(16px, 4vw, 48px)',
    flex: 1,
    overflow: 'hidden' as const,
  },
  column: {
    flex: 1,
    overflow: 'hidden' as const,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  sectionTitle: {
    fontSize: 'clamp(16px, 2.5vw, 28px)',
    color: '#c9a84c',
    margin: '0 0 12px 0',
    fontWeight: 600,
  },
  eventCard: {
    padding: '12px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  eventName: {
    fontSize: 'clamp(14px, 2vw, 24px)',
    color: '#ffffff',
    fontWeight: 600,
    margin: 0,
  },
  eventMeta: {
    fontSize: 'clamp(12px, 1.7vw, 20px)',
    color: '#e0e0e0',
    margin: '4px 0 0 0',
  },
  agendaItem: {
    padding: '10px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  agendaTitle: {
    fontSize: 'clamp(14px, 2vw, 24px)',
    color: '#ffffff',
    margin: 0,
  },
  agendaSpeaker: {
    fontSize: 'clamp(12px, 1.7vw, 20px)',
    color: '#c9a84c',
    margin: '4px 0 0 0',
  },
  agendaTime: {
    fontSize: 'clamp(12px, 1.7vw, 20px)',
    color: '#e0e0e0',
    margin: '2px 0 0 0',
  },
  emptyText: {
    fontSize: 'clamp(14px, 1.8vw, 22px)',
    color: '#e0e0e0',
    fontStyle: 'italic' as const,
  },
};

function formatEventTime(timeStr: string): string {
  if (!timeStr) return '';
  // Extract HH:MM from stored value (stored as Eastern, no conversion needed)
  let hours: number;
  let minutes: number;

  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function formatTimeRange(start: string, end: string): string {
  try {
    const s = formatEventTime(start);
    const e = formatEventTime(end);
    return `${s} – ${e}`;
  } catch {
    return '';
  }
}

function EventCard({ event }: { event: EventSummary }) {
  return (
    <div style={styles.eventCard}>
      <p style={styles.eventName}>{event.name}</p>
      <p style={{ ...styles.eventMeta, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Clock size={18} color="#c9a84c" />
        {formatTimeRange(event.startTime, event.endTime)}
        <MapPin size={18} color="#c9a84c" style={{ marginLeft: '8px' }} />
        {event.venueName}
      </p>
    </div>
  );
}

function AgendaCard({ item }: { item: AgendaItemSummary }) {
  return (
    <div style={styles.agendaItem}>
      <p style={{ ...styles.agendaTitle, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ListChecks size={20} color="#c9a84c" />
        {item.title}
      </p>
      {item.speaker && <p style={styles.agendaSpeaker}>{item.speaker}</p>}
      <p style={styles.agendaTime}>
        {formatTimeRange(item.startTime, item.endTime)}
      </p>
    </div>
  );
}

/**
 * Events area panel — shows current events, upcoming events, and active agenda items.
 */
export function EventsPanel({ data }: EventsPanelProps) {
  const eventsData = data as EventsDashboardData;

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>
        <CalendarDays size={42} style={{ marginRight: 16, verticalAlign: 'middle' }} />
        Events
      </h1>

      <div style={styles.columnsRow}>
        <div style={styles.column}>
          <h2 style={styles.sectionTitle}>Happening Now</h2>
          {eventsData.currentEvents.length > 0 ? (
            eventsData.currentEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))
          ) : (
            <p style={styles.emptyText}>No events in progress</p>
          )}

          <h2 style={{ ...styles.sectionTitle, marginTop: '24px' }}>
            Coming Up
          </h2>
          {eventsData.upcomingEvents.length > 0 ? (
            eventsData.upcomingEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))
          ) : (
            <p style={styles.emptyText}>No upcoming events today</p>
          )}
        </div>

        <div style={styles.column}>
          <h2 style={styles.sectionTitle}>Agenda</h2>
          {eventsData.activeAgendaItems.length > 0 ? (
            eventsData.activeAgendaItems.map((item) => (
              <AgendaCard key={item.id} item={item} />
            ))
          ) : (
            <p style={styles.emptyText}>No active agenda items</p>
          )}
        </div>
      </div>
    </div>
  );
}
